import {
  buildApplyStageRoutePath,
  useDefineProviderRoute,
  useExperienceType,
  usePrimaryProvider,
  useSelectedPlan,
} from "@versatile-credit-apply/composition"
import { type RouteRecordRaw } from "vue-router"
import {
  ScreenId,
  type StaticProviderKey,
} from "@versatile-credit-apply/omni-apply-client"
import {
  useApplicationStore,
  useCurrentProviderKey,
  useRequireCurrentProviderKey,
} from "@versatile-credit-apply/sdk"

const providerKey: StaticProviderKey = "hfd_standard"
const { defineRoute } = useDefineProviderRoute(providerKey)

export const routes: RouteRecordRaw[] = [
  defineRoute({
    name: "hfd.apply.form.application.done",
    path: "/apply/:applyStage/forms/application/done",
    redirect(to) {
      const primaryProvider = usePrimaryProvider()
      const currentProviderKey = useRequireCurrentProviderKey()

      if (primaryProvider.value?.providerKey === currentProviderKey.value) {
        return buildApplyStageRoutePath(
          to.params.providerKey as string,
          "/terms",
          {
            params: to.params,
          }
        )
      }

      // Submit the form if not the primary
      return buildApplyStageRoutePath(providerKey, "/submit", {
        params: to.params,
      })
    },
  }),
  defineRoute({
    path: "/apply/:applyStage/responses/approved",
    inherit: true,
    component: () =>
      import("@/providers/hfd_standard/views/HFDApprovedView.vue"),
  }),
  defineRoute({
    path: "/apply/:applyStage/responses/prequalified",
    inherit: true,
    component: () => import("@/views/apply/ExploreOffersView.vue"),
    meta: {
      screenId: ScreenId.OFFER_PRESENTATION_PREQUAL,
    },
  }),
  defineRoute({
    path: "/apply/:applyStage/responses/error",
    inherit: true,
    component: () => import("@/providers/hfd_standard/views/HFDErrorView.vue"),
  }),
  defineRoute({
    path: "/apply/:applyStage/selected",
    component: () =>
      import("@/providers/hfd_standard/views/HFDOfferSelectedView.vue"),
  }),
  defineRoute({
    path: "/apply/:applyStage/additional/information",
    component: () =>
      import("@/providers/hfd_standard/views/HFDAdditionalInformationView.vue"),
    beforeEnter: () => {
      const appStore = useApplicationStore()
      const primaryMobilePhone = appStore.application?.primary?.mobilePhone
      const primaryEmail = appStore.application?.primary?.email
      const provider = useCurrentProviderKey()
      const experienceType = useExperienceType()
      // If we already have the primary's mobile phone and email, we can skip this step
      if (primaryMobilePhone && primaryEmail) {
        return `/providers/${provider.value}/${experienceType.value}/apply/offer/payment/down-payment`
      }
      return true
    },
  }),
  defineRoute({
    path: "/apply/:applyStage/payment/down-payment",
    component: () =>
      import("@/providers/hfd_standard/views/HFDDownPaymentView.vue"),
    beforeEnter: () => {
      const provider = useCurrentProviderKey()
      const experienceType = useExperienceType()
      const selectedPlan = useSelectedPlan()
      const downPaymentCents = selectedPlan.value?.downPaymentCents ?? 0
      if (downPaymentCents > 0) {
        return true
      }
      return `/providers/${provider.value}/${experienceType.value}/apply/offer/payment/recurring-payment`
    },
  }),
  defineRoute({
    path: "/apply/:applyStage/payment/recurring-payment",
    component: () =>
      import("@/providers/hfd_standard/views/HFDRecurringPaymentView.vue"),
  }),
  defineRoute({
    path: "/apply/offer/terms",
    inherit: true,
    component: () => import("@/providers/hfd_standard/views/HFDOfferTerms.vue"),
  }),
  defineRoute({
    path: "/apply/:applyStage/:planCode/review",
    inherit: true,
    redirect: (to) => {
      return buildApplyStageRoutePath(
        to.params.providerKey as string,
        "/selected",
        { params: { ...to.params, applyStage: "offer" } }
      )
    },
  }),
]
